/**
 * @file api-returns.ts
 * @description API-клиент Returns (phase10.5 шаг 3): расширенный ReturnDto
 * со стадией, CRUD pipelines/stages, transition, labels.
 *
 * `apiFetch<T>()` уже разворачивает `{data}` обёртку — здесь не оборачиваем
 * повторно.
 */

import { apiFetch } from "./api-client";
import type { ReturnType, ReturnItemCondition } from "./api-orders";

// ─── Enums + labels ────────────────────────────────────────────────────────

export type ReturnStageKind =
  | "REQUESTED"
  | "APPROVED"
  | "AWAITING_GOODS"
  | "INSPECTION"
  | "APPROVED_REFUND"
  | "REFUNDED"
  | "REJECTED";

/** Fallback-метки kind'ов — магазин может задать своё name стадии. */
export const RETURN_STAGE_KIND_LABEL: Record<ReturnStageKind, string> = {
  REQUESTED: "Запрошен",
  APPROVED: "Согласован",
  AWAITING_GOODS: "Ждём посылку",
  INSPECTION: "На проверке",
  APPROVED_REFUND: "К возврату",
  REFUNDED: "Возвращён",
  REJECTED: "Отказан",
};

export const RETURN_STAGE_KIND_TONE: Record<
  ReturnStageKind,
  "default" | "secondary" | "destructive" | "outline"
> = {
  REQUESTED: "destructive",
  APPROVED: "secondary",
  AWAITING_GOODS: "secondary",
  INSPECTION: "secondary",
  APPROVED_REFUND: "default",
  REFUNDED: "default",
  REJECTED: "destructive",
};

/** SLA в днях по ЗоЗПП ст.22 на возврат денег. */
export const REFUND_DEADLINE_DAYS = 10;

// ─── DTO ────────────────────────────────────────────────────────────────────

export interface ReturnStageDto {
  id: string;
  code: string;
  name: string;
  kind: ReturnStageKind;
  color: string | null;
  slaHours: number | null;
  sortOrder: number;
}

export interface ReturnDto {
  id: string;
  orderId: string;
  orderNumber: string;
  type: ReturnType;
  /** @deprecated phase10.5 — use stage.kind. */
  status: string;
  /** phase10.5 шаг 3 — текущая стадия. */
  stage: ReturnStageDto | null;
  pipelineId: string | null;
  customerComment: string | null;
  internalComment: string | null;
  refundAmount: string | null;
  requestedAt: string;
  approvedAt: string | null;
  completedAt: string | null;
  rejectedReason: string | null;
  itemsCount: number;
  createdAt: string;
}

export interface ReturnPipelineWithStagesDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  stages: (ReturnStageDto & { isActive: boolean; returnsCount: number })[];
}

/** Полное DTO стадии для страницы настроек (с isActive / returnsCount / timestamps). */
export interface ReturnStageConfigDto {
  id: string;
  pipelineId: string;
  code: string;
  name: string;
  kind: ReturnStageKind;
  color: string | null;
  slaHours: number | null;
  sortOrder: number;
  isActive: boolean;
  returnsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReturnStagePayload {
  code: string;
  name: string;
  kind: ReturnStageKind;
  color?: string;
  slaHours?: number;
  sortOrder?: number;
}

export interface UpdateReturnStagePayload {
  code?: string;
  name?: string;
  kind?: ReturnStageKind;
  color?: string;
  slaHours?: number;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CreateReturnPayload {
  type: ReturnType;
  customerComment?: string;
  internalComment?: string;
  items: {
    orderItemId: string;
    quantity: string;
    condition?: ReturnItemCondition;
    unitRefund?: string;
  }[];
}

export interface ReturnStageHistoryEntryDto {
  id: string;
  changedAt: string;
  fromStage: { id: string; name: string; kind: ReturnStageKind } | null;
  toStage: { id: string; name: string; kind: ReturnStageKind };
  changedBy: { id: string; displayName: string | null };
  note: string | null;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export async function listReturnPipelines(): Promise<
  ReturnPipelineWithStagesDto[]
> {
  return apiFetch("/admin/returns/pipelines");
}

export async function listReturns(params?: {
  stageKind?: ReturnStageKind;
  stageId?: string;
  pipelineId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: ReturnDto[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.stageKind) qs.set("stageKind", params.stageKind);
  if (params?.stageId) qs.set("stageId", params.stageId);
  if (params?.pipelineId) qs.set("pipelineId", params.pipelineId);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const s = qs.toString();
  return apiFetch(`/admin/returns${s ? `?${s}` : ""}`);
}

export async function listReturnsByOrder(orderId: string): Promise<ReturnDto[]> {
  return apiFetch(`/admin/orders/${orderId}/returns`);
}

export async function getReturn(id: string): Promise<ReturnDto> {
  return apiFetch(`/admin/returns/${id}`);
}

export async function createReturn(
  orderId: string,
  payload: CreateReturnPayload,
): Promise<ReturnDto> {
  return apiFetch(`/admin/orders/${orderId}/returns`, {
    method: "POST",
    json: payload,
  });
}

/** phase10.5 шаг 3 — универсальный переход по toStageId (drag-drop). */
export async function transitionReturnToStage(
  returnId: string,
  input: {
    toStageId: string;
    note?: string;
    rejectedReason?: string;
    refundAmount?: string;
  },
): Promise<ReturnDto> {
  return apiFetch(`/admin/returns/${returnId}/transition`, {
    method: "POST",
    json: input,
  });
}

export const returnAction = {
  approve: (id: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/approve`, { method: "POST" }),
  inspect: (id: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/inspect`, { method: "POST" }),
  approveRefund: (id: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/approve-refund`, {
      method: "POST",
    }),
  complete: (id: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/complete`, { method: "POST" }),
  reject: (id: string, reason: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/reject`, {
      method: "POST",
      json: { reason },
    }),
};

// ─── Return Stages CRUD ────────────────────────────────────────────────────

export async function listReturnStages(
  pipelineId: string,
): Promise<ReturnStageConfigDto[]> {
  return apiFetch(`/admin/returns/pipelines/${pipelineId}/stages`);
}

export async function createReturnStage(
  pipelineId: string,
  payload: CreateReturnStagePayload,
): Promise<ReturnStageConfigDto> {
  return apiFetch(`/admin/returns/pipelines/${pipelineId}/stages`, {
    method: "POST",
    json: payload,
  });
}

export async function updateReturnStage(
  pipelineId: string,
  stageId: string,
  payload: UpdateReturnStagePayload,
): Promise<ReturnStageConfigDto> {
  return apiFetch(
    `/admin/returns/pipelines/${pipelineId}/stages/${stageId}`,
    { method: "PATCH", json: payload },
  );
}

export async function deleteReturnStage(
  pipelineId: string,
  stageId: string,
): Promise<void> {
  await apiFetch(
    `/admin/returns/pipelines/${pipelineId}/stages/${stageId}`,
    { method: "DELETE" },
  );
}

export async function reorderReturnStages(
  pipelineId: string,
  items: { id: string; sortOrder: number }[],
): Promise<ReturnStageConfigDto[]> {
  return apiFetch(
    `/admin/returns/pipelines/${pipelineId}/stages/reorder`,
    { method: "POST", json: { items } },
  );
}

export async function listReturnStageHistory(
  returnId: string,
): Promise<ReturnStageHistoryEntryDto[]> {
  return apiFetch(`/admin/returns/${returnId}/stage-history`);
}
